import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiGet } from '../utils/apiUtils';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import {
    FiActivity, FiCheckCircle, FiX, FiTrendingUp, FiFilter, 
    FiSearch, FiChevronLeft, FiChevronRight, FiCheck, FiBarChart2,
    FiMonitor
} from 'react-icons/fi';
import { FaFire } from 'react-icons/fa';

const LANGUAGE_MAP = {
    63: { name: 'JavaScript', logo: 'javascript.png' },
    71: { name: 'Python', logo: 'python.png' },
    62: { name: 'Java', logo: 'java.png' },
    52: { name: 'C++', logo: 'cpp.png' }
};

const MySubmissions = ({ navigate }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Submissions data
    const [submissions, setSubmissions] = useState([]);
    const [filteredSubmissions, setFilteredSubmissions] = useState([]);

    // Filter states
    const [statusFilter, setStatusFilter] = useState('all');
    const [difficultyFilter, setDifficultyFilter] = useState('all');
    const [languageFilter, setLanguageFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const submissionsPerPage = 15;

    const { isAuthenticated, user } = useAuth();

    // Fetch submissions data
    const fetchSubmissions = async (currentUser) => {
        if (!currentUser?.id) {
            setError('Authentication required');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const response = await apiGet(`/auth/submissions/${currentUser.id}?limit=1000`);

            if (response.ok) {
                const data = await response.json();
                setSubmissions(data.submissions || []);
                setFilteredSubmissions(data.submissions || []);
            } else {
                throw new Error('Failed to fetch submissions');
            }

        } catch (err) {
            setError('Failed to load submissions');
        } finally {
            setLoading(false);
        }
    };

    // Filter submissions
    useEffect(() => {
        let filtered = submissions;

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(sub => {
                if (statusFilter === 'Accepted') return sub.status === 'Accepted';
                if (statusFilter === 'Failed' || statusFilter === 'Wrong Answer') return sub.status === 'Wrong Answer' || sub.status === 'Failed';
                return sub.status.includes(statusFilter);
            });
        }

        // Difficulty filter
        if (difficultyFilter !== 'all') {
            filtered = filtered.filter(sub => sub.questions?.difficulty?.toLowerCase() === difficultyFilter);
        }

        // Language filter
        if (languageFilter !== 'all') {
            filtered = filtered.filter(sub => sub.language_id === languageFilter);
        }

        // Search filter
        if (searchQuery) {
            filtered = filtered.filter(sub =>
                sub.questions?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                sub.questions?.topic?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        setFilteredSubmissions(filtered);
        setCurrentPage(1);
    }, [submissions, statusFilter, difficultyFilter, languageFilter, searchQuery]);

    // Get current page submissions
    const getCurrentPageSubmissions = () => {
        const startIndex = (currentPage - 1) * submissionsPerPage;
        const endIndex = startIndex + submissionsPerPage;
        return filteredSubmissions.slice(startIndex, endIndex);
    };

    const getTotalPages = () => {
        return Math.ceil(filteredSubmissions.length / submissionsPerPage) || 1;
    };

    // Initial data fetch
    useEffect(() => {
        if (isAuthenticated && user?.id) {
            fetchSubmissions(user);
        } else {
            setLoading(false);
            setError("Authentication required to view submissions.");
        }
    }, [isAuthenticated, user]);

    // Helper functions
    const getDifficultyColor = (difficulty) => {
        switch (difficulty?.toLowerCase()) {
            case 'easy': return 'text-emerald-600';
            case 'medium': return 'text-amber-600';
            case 'hard': return 'text-red-600';
            default: return 'text-slate-500';
        }
    };

    const getStatusColor = (status) => {
        if (status.includes('Accepted')) return 'text-emerald-600';
        if (status.includes('Wrong Answer') || status.includes('Failed')) return 'text-red-600';
        if (status.includes('Time Limit Exceeded')) return 'text-purple-600';
        if (status.includes('Runtime Error')) return 'text-cyan-600';
        if (status.includes('Compilation Error')) return 'text-amber-600';
        return 'text-slate-500';
    };

    const getStatusBgColor = (status) => {
        if (status.includes('Accepted')) return 'text-green-600';
        if (status.includes('Wrong Answer') || status.includes('Failed')) return 'text-red-600';
        if (status.includes('Time Limit Exceeded')) return 'text-purple-600';
        if (status.includes('Runtime Error')) return 'text-cyan-600';
        if (status.includes('Compilation Error')) return 'text-yellow-600';
        return 'text-slate-500';
    };

    const getLanguageInfo = (languageId) => {
        return LANGUAGE_MAP[languageId] || { name: 'Unknown', logo: null };
    };

    const formatDateIST = (dateString) => {
        if (!dateString) return 'Unknown Date';
        
        // Ensure the string is treated as UTC if it doesn't have timezone info
        let ds = dateString;
        if (!ds.includes('Z') && !ds.includes('+') && !ds.match(/-\d{2}:\d{2}$/)) {
            ds = ds.replace(' ', 'T') + 'Z';
        }

        const date = new Date(ds);
        return date.toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    // Get unique values for filters
    const getUniqueLanguages = () => {
        const languageIds = [...new Set(submissions.map(sub => sub.language_id))];
        return languageIds.map(id => ({
            id: id,
            ...getLanguageInfo(id)
        })).filter(lang => lang.name !== 'Unknown');
    };

    const navLinks = [
        { id: "home", label: "Home", action: () => navigate("/") },
        { id: "learning", label: "Learning", action: () => navigate("/learning") },
        { id: "dashboard", label: "Dashboard", action: () => navigate("/dashboard"), show: isAuthenticated },
        { id: "dsa", label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => navigate("/system-design") }
    ].filter(link => link.show !== false);

    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { label: "System Design", action: () => navigate("/system-design") }
    ];

    // Stats calculations
    const totalSubmissions = submissions.length;
    const acceptedCount = submissions.filter(sub => sub.status === 'Accepted').length;
    const failedCount = totalSubmissions - acceptedCount;
    const successRate = totalSubmissions > 0 ? Math.round((acceptedCount / totalSubmissions) * 100) : 0;

    // Loading state
    if (loading) {
        return <MySubmissionsSkeleton />;
    }

    return (
        <div className="min-h-screen bg-accent text-foreground font-sans flex flex-col w-full overflow-x-hidden">
            <Navbar navigate={navigate} navLinks={navLinks} showAuthActions={true} />
            
            <main className="flex-1 pt-16">
                
                {/* ── Header ── */}
                <section className="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 mt-4 mb-8 py-4 hidden md:block">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <h1 className="text-left text-3xl font-bold text-foreground underline decoration-dotted decoration-foreground underline-offset-8">
                                My <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent underline decoration-dotted decoration-emerald-600 ml-1">Submissions</span>
                            </h1>
                            <p className="text-muted-foreground text-sm font-medium tracking-wide hidden sm:block">Track your coding progress</p>
                        </div>
                    </div>
                </section>

                <div className="md:hidden flex flex-col items-center justify-center py-20 px-6 text-center">
                    <div className="bg-white p-8 rounded shadow-sm border border-emerald-100 max-w-sm w-full">
                        <FiMonitor className="text-6xl text-emerald-600 mb-6 mx-auto opacity-80" />
                        <h3 className="text-xl font-bold text-slate-800 mb-3 tracking-tight">PC View Required</h3>
                        <p className="text-slate-500 text-sm leading-relaxed mb-1">
                            Your Recent Submissions and detailed analytics are currently only available in <span className="font-semibold text-slate-800">PC view</span>
                        </p>
                        {/* <p className="text-slate-400 text-[13px]">Please switch to a desktop for the full experience.</p> */}
                    </div>
                </div>

                <div className="hidden md:block max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 pb-20">
                    
                    {/* ── 4-Column Banner ── */}
                    <div className="bg-emerald-600 rounded px-4 md:px-6 py-2 md:py-4 mb-8 border border-emerald-700/50 shadow-lg">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
                            {/* Total */}
                            <div className="flex flex-col items-center justify-center py-6 border-b border-r md:border-b-0 md:border-r-0 border-emerald-100/20">
                                <span className="text-emerald-100 text-sm md:text-lg font-medium tracking-wide mb-1 flex items-center gap-2"><FiActivity className="text-emerald-100" /> Total</span>
                                <div className="text-3xl md:text-4xl font-black text-white">{totalSubmissions}</div>
                            </div>
                            {/* Accepted */}
                            <div className="flex flex-col items-center justify-center py-6 border-b md:border-b-0 md:border-l border-emerald-100/20">
                                <span className="text-emerald-100 text-sm md:text-lg font-medium tracking-wide mb-1 flex items-center gap-2"><FiCheck className="text-emerald-100" /> Accepted</span>
                                <div className="text-3xl md:text-4xl font-black text-white">{acceptedCount}</div>
                            </div>
                            {/* Failed */}
                            <div className="flex flex-col items-center justify-center py-6 border-r md:border-r-0 md:border-l border-emerald-100/20">
                                <span className="text-emerald-100 text-sm md:text-lg font-medium tracking-wide mb-1 flex items-center gap-2"><FiX className="text-emerald-100" /> Failed</span>
                                <div className="text-3xl md:text-4xl font-black text-white">{failedCount}</div>
                            </div>
                            {/* Success Rate */}
                            <div className="flex flex-col items-center justify-center py-6 md:border-l border-emerald-100/20">
                                <span className="text-emerald-100 text-sm md:text-lg font-medium tracking-wide mb-1 flex items-center gap-2"><FiTrendingUp className="text-emerald-100" /> Success Rate</span>
                                <div className="text-3xl md:text-4xl font-black text-white">{successRate}%</div>
                            </div>
                        </div>
                    </div>

                    {/* ── Sticky Filter Bar ── */}
                    <div className="sticky top-16 z-30 bg-accent pt-2 pb-6">
                        <div className="bg-white rounded border-l-4 border-emerald-600 p-4 space-y-4">
                            
                            {/* Top row: Search | Difficulty | Accepted/Failed | Language */}
                            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                                
                                {/* Search */}
                                <div className="relative w-full lg:w-80 flex-shrink-0">
                                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-emerald-600 w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Search by problem title..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full bg-accent text-foreground text-sm rounded-t pl-10 pr-4 py-2.5 outline-none border-t-0 border-x-0 border-b-2 border-transparent focus:outline-none focus:ring-0 focus:border-emerald-600 transition-colors placeholder:text-muted-foreground/70"
                                    />
                                </div>

                                {/* Language Filter with Logos */}
                                <div className="flex items-center justify-start gap-4 w-full lg:w-auto overflow-x-auto pb-2 lg:pb-0 flex-shrink-0 md:border-l md:border-border md:pl-5 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                    {Object.entries(LANGUAGE_MAP).map(([langIdStr, langInfo]) => {
                                        const langId = parseInt(langIdStr);
                                        return (
                                            <button
                                                key={langId}
                                                onClick={() => setLanguageFilter(languageFilter === langId ? 'all' : langId)}
                                                className="flex items-center transition-all whitespace-nowrap"
                                            >
                                                {langInfo.logo && (
                                                    <img 
                                                        src={`/languages/${langInfo.logo}`} 
                                                        alt={langInfo.name} 
                                                        className={`w-8 h-8 transition-all duration-300 
                                                            ${languageFilter === langId 
                                                                ? 'grayscale-0 opacity-100 drop-shadow-md' 
                                                                : 'grayscale opacity-100 hover:grayscale-0 hover:opacity-100'
                                                            }`} 
                                                    />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Difficulty */}
                                <div className="flex items-center justify-start gap-3 w-full lg:w-auto overflow-x-auto pb-2 lg:pb-0 flex-shrink-0 md:border-l md:border-border md:pl-5 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                    {['easy', 'medium', 'hard'].map((diff) => (
                                        <button
                                            key={diff}
                                            onClick={() => setDifficultyFilter(difficultyFilter === diff ? 'all' : diff)}
                                            className={`px-3 py-1.5 rounded text-xs font-semibold capitalize transition-all whitespace-nowrap border
                                                ${difficultyFilter === diff
                                                    ? (diff === 'easy' ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm' :
                                                        diff === 'medium' ? 'bg-yellow-600 text-white border-yellow-600 shadow-sm' :
                                                            'bg-red-600 text-white border-red-600 shadow-sm')
                                                    : 'bg-accent text-muted-foreground hover:bg-emerald-50 hover:text-emerald-700 border-transparent'}`}
                                        >
                                            {diff}
                                        </button>
                                    ))}
                                </div>

                                {/* Status Filter */}
                                <div className="flex items-center justify-start gap-3 w-full lg:w-auto overflow-x-auto pb-2 lg:pb-0 flex-shrink-0 md:border-l md:border-border md:pl-4 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                    {['Accepted', 'Failed', 'Compilation Error', 'Runtime Error', 'Time Limit Exceeded'].map((status) => (
                                        <button
                                            key={status}
                                            onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
                                            className={`px-3 py-1.5 rounded text-xs font-semibold capitalize transition-all whitespace-nowrap border
                                                ${statusFilter === status
                                                    ? 'bg-slate-800 text-white border-slate-800 shadow-sm'
                                                    : 'bg-accent text-muted-foreground hover:bg-emerald-50 hover:text-emerald-700 border-transparent'}`}
                                        >
                                            {status}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Submissions Table ── */}
                    <div className="bg-white rounded border-l-4 border-emerald-600 flex flex-col">
                        {/* Table Header */}
                        <div className="divide-y divide-gray-300">
                            {(() => {
                                const currentSubmissions = getCurrentPageSubmissions();
                                if (currentSubmissions.length === 0) {
                                    return (
                                        <div className="text-center py-16">
                                            <FiSearch className="text-4xl text-muted-foreground mx-auto mb-3" />
                                            <h3 className="text-lg font-medium text-muted-foreground mb-1">No Submissions Found</h3>
                                        </div>
                                    );
                                }
                                return currentSubmissions.map((sub) => {
                                    const langInfo = getLanguageInfo(sub.language_id);
                                    const isAccepted = sub.status === 'Accepted';
                                    
                                    return (
                                        <div key={sub.id} className="flex flex-col md:flex-row md:items-center justify-between p-4 md:px-2 hover:bg-accent/50 transition-colors group">
                                            
                                            {/* ID */}
                                            <div className="hidden md:flex shrink-0 w-16 justify-center text-left">
                                                <span className="text-emerald-600 font-bold text-sm tracking-wider">#{sub.questions?.id}</span>
                                            </div>

                                            {/* Mobile ID + Title */}
                                            <div className="flex-1 min-w-0 md:px-4 mb-3 md:mb-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="md:hidden text-emerald-600 font-bold text-xs">#{sub.questions?.id}</span>
                                                    <h3 
                                                        className="text-foreground font-semibold text-[15px] truncate hover:underline cursor-pointer"
                                                        onClick={() => navigate(`/problems/${sub.questions?.id}`)}
                                                    >
                                                        {sub.questions?.title || 'Unknown Problem'}
                                                    </h3>
                                                </div>
                                                <div className="md:hidden flex flex-wrap gap-2 text-xs text-slate-500 mt-1">
                                                    <span className={`capitalize font-bold ${getDifficultyColor(sub.questions?.difficulty)}`}>{sub.questions?.difficulty}</span>
                                                    <span>•</span>
                                                    <span className={`font-bold ${getStatusColor(sub.status)}`}>{sub.status}</span>
                                                </div>
                                            </div>

                                            {/* Topic */}
                                            <div className="hidden md:flex w-32 shrink-0 items-center text-left">
                                                <span className="text-xs text-muted-foreground truncate block capitalize">
                                                    {sub.questions?.topic || 'Unknown Topic'}
                                                </span>
                                            </div>

                                            {/* Difficulty */}
                                            <div className="hidden md:flex w-24 shrink-0 items-center text-left">
                                                <span className={`text-xs font-bold capitalize ${getDifficultyColor(sub.questions?.difficulty)}`}>
                                                    {sub.questions?.difficulty || 'Unknown'}
                                                </span>
                                            </div>

                                            {/* Status */}
                                            <div className="hidden md:flex w-40 shrink-0 items-center text-left">
                                                <div className={`flex items-center gap-1.5 text-xs font-bold truncate max-w-full ${getStatusBgColor(sub.status)}`} title={sub.status}>
                                                    
                                                    <span className="truncate">{sub.status.includes('Compilation') ? 'Compilation Error' : sub.status.includes('Runtime') ? 'Runtime Error' : sub.status.includes('Time Limit') ? 'TLE' : sub.status}</span>
                                                </div>
                                            </div>

                                            {/* Language Logo */}
                                            <div className="hidden md:flex w-32 shrink-0 items-center justify-center gap-2">
                                                {langInfo.logo && <img src={`/languages/${langInfo.logo}`} alt={langInfo.name} className="w-8 h-8" />}
                                            </div>

                                            {/* Date */}
                                            <div className="w-full md:w-40 shrink-0 text-left md:text-right flex items-center md:justify-end mt-2 md:mt-0">
                                                <span className="text-xs font-medium text-slate-500 bg-gray-50 md:bg-transparent px-2 py-1 md:p-0 rounded border border-gray-100 md:border-transparent">
                                                    {formatDateIST(sub.created_at)}
                                                </span>
                                            </div>

                                        </div>
                                    );
                                });
                            })()}
                        </div>
                        
                        {/* Pagination */}
                        {filteredSubmissions.length > submissionsPerPage && (
                            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div className="text-sm font-medium text-slate-500">
                                    Showing <span className="text-slate-800 font-bold">{((currentPage - 1) * submissionsPerPage) + 1}</span> to <span className="text-slate-800 font-bold">{Math.min(currentPage * submissionsPerPage, filteredSubmissions.length)}</span> of <span className="text-slate-800 font-bold">{filteredSubmissions.length}</span> results
                                </div>
                                <div className="flex items-center justify-end gap-2">
                                    <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="flex items-center justify-center w-8 h-8 bg-white border border-gray-200 text-slate-600 rounded hover:bg-gray-50 hover:text-emerald-600 disabled:opacity-40 disabled:hover:bg-white transition-all shadow-sm"><FiChevronLeft className="w-4 h-4" /></button>
                                    <div className="flex items-center gap-1.5 px-2">
                                        <span className="text-emerald-700 text-sm font-bold">{currentPage}</span>
                                        <span className="text-slate-400 text-xs font-bold">/</span>
                                        <span className="text-slate-500 text-xs font-bold">{getTotalPages()}</span>
                                    </div>
                                    <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, getTotalPages()))} disabled={currentPage === getTotalPages()} className="flex items-center justify-center w-8 h-8 bg-white border border-gray-200 text-slate-600 rounded hover:bg-gray-50 hover:text-emerald-600 disabled:opacity-40 disabled:hover:bg-white transition-all shadow-sm"><FiChevronRight className="w-4 h-4" /></button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
            
            <Footer navigate={navigate} navLinks={footerLinks} />
        </div>
    );
};

const MySubmissionsSkeleton = () => {
    return (
        <div className="min-h-screen bg-accent text-foreground font-sans flex flex-col">
            <div className="fixed top-0 w-full h-16 bg-emerald-900 z-50"></div>
            
            <div className="flex-1 max-w-8xl mx-auto w-full pt-24 px-4 sm:px-12 lg:px-16 pb-20 animate-pulse">
                {/* Header Skeleton */}
                <div className="h-10 w-64 bg-gray-200 rounded mb-8"></div>
                
                {/* Banner Skeleton */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8 h-32 flex items-center justify-around">
                    <div className="h-12 w-20 bg-gray-100 rounded"></div>
                    <div className="h-12 w-20 bg-gray-100 rounded"></div>
                    <div className="h-12 w-20 bg-gray-100 rounded"></div>
                    <div className="h-12 w-20 bg-gray-100 rounded"></div>
                </div>

                {/* Filter Bar Skeleton */}
                <div className="bg-white rounded border-l-4 border-emerald-600 p-4 mb-6 h-16 flex justify-between items-center">
                    <div className="h-8 w-64 bg-gray-100 rounded"></div>
                    <div className="h-8 w-48 bg-gray-100 rounded"></div>
                    <div className="h-8 w-40 bg-gray-100 rounded"></div>
                </div>

                {/* Table Skeleton */}
                <div className="bg-white rounded shadow-sm border border-gray-200 p-4 space-y-4">
                    {Array.from({ length: 10 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-4 py-3">
                            <div className="h-4 w-12 bg-gray-200 rounded"></div>
                            <div className="flex-1 space-y-2">
                                <div className="h-4 w-1/3 bg-gray-200 rounded"></div>
                            </div>
                            <div className="h-4 w-20 bg-gray-100 rounded"></div>
                            <div className="h-6 w-24 bg-gray-100 rounded-full"></div>
                            <div className="h-4 w-24 bg-gray-100 rounded"></div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MySubmissions;